import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, RefreshCw, ScrollText } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { EUR: "€", GBP: "£", USD: "$", AED: "د.إ" };

const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card",          label: "Card" },
  { value: "cash",          label: "Cash" },
  { value: "tikkie",        label: "Tikkie" },
  { value: "other",         label: "Other" },
];

const METHOD_LABELS = Object.fromEntries(PAYMENT_METHODS.map(m => [m.value, m.label]));

function fmt(amount, currency = "EUR") {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  return `${sym}${Number(amount || 0).toFixed(2)}`;
}

export default function ReceiptList() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [receipts, setReceipts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filterMethod, setFilterMethod] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMethod !== "all") params.set("payment_method", filterMethod);
      const res = await axios.get(`${API}/receipts?${params}`, { headers: getAuthHeaders() });
      setReceipts(res.data || []);
    } catch {
      toast.error("Failed to load receipts");
    } finally {
      setLoading(false);
    }
  }, [filterMethod]);

  useEffect(() => { load(); }, [load]);

  const filtered = receipts.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.receipt_number  || "").toLowerCase().includes(q) ||
      (r.invoice_number  || "").toLowerCase().includes(q) ||
      (r.customer_name   || "").toLowerCase().includes(q)
    );
  });

  const totalReceived = filtered.reduce((s, r) => s + (Number(r.amount_received) || 0), 0);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl font-bold text-slate-900">Receipts</h1>
            <p className="text-sm text-slate-500 mt-0.5">{filtered.length} receipt{filtered.length !== 1 ? "s" : ""}</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Summary card */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest">Total Received</p>
            <p className="text-xl font-bold font-mono mt-1 text-green-700">€{totalReceived.toFixed(2)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest">Receipts Issued</p>
            <p className="text-xl font-bold font-mono mt-1 text-slate-700">{filtered.length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search receipt #, invoice #, customer…"
              className="pl-9 h-9 text-sm" />
          </div>
          <Select value={filterMethod} onValueChange={setFilterMethod}>
            <SelectTrigger className="h-9 text-sm w-44"><SelectValue placeholder="Payment Method" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              {PAYMENT_METHODS.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <ScrollText className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">No receipts found</p>
              <p className="text-xs mt-1">Receipts are created automatically when payments are recorded</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    {["Receipt #", "Invoice #", "Customer", "Payment Date", "Amount", "Method", "Reference", "Remaining Balance"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={r.id}
                      className={`border-b border-slate-50 hover:bg-purple-50/40 cursor-pointer transition-colors ${i % 2 === 1 ? "bg-slate-50/30" : ""}`}
                      onClick={() => navigate(`/receipts/${r.id}`)}>
                      <td className="px-4 py-3 font-mono text-xs font-bold text-purple-700 whitespace-nowrap">
                        {r.receipt_number}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={e => { e.stopPropagation(); navigate(`/invoices/${r.invoice_id}`); }}
                          className="font-mono text-xs font-semibold text-blue-600 hover:underline whitespace-nowrap">
                          {r.invoice_number}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800 max-w-[160px] truncate">
                        {r.customer_name || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {r.payment_date ? format(new Date(r.payment_date), "d MMM yyyy") : "—"}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm font-semibold text-green-700 whitespace-nowrap">
                        {fmt(r.amount_received, r.currency)}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                        {METHOD_LABELS[r.payment_method] || r.payment_method || "—"}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap max-w-[120px] truncate">
                        {r.payment_reference || "—"}
                      </td>
                      <td className={`px-4 py-3 font-mono text-sm font-semibold whitespace-nowrap ${Number(r.remaining_balance) > 0 ? "text-amber-700" : "text-green-700"}`}>
                        {fmt(r.remaining_balance, r.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </DashboardLayout>
  );
}
